import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useSearch } from "@/context/SearchContext";
import { Desktop, HardDrives, ShieldSlash, ArrowsClockwise, MagnifyingGlass, Prohibit, ArrowsCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS = {
  healthy: { color: "#3ECF8E", label: "HEALTHY" },
  at_risk: { color: "#FFCC00", label: "AT RISK" },
  compromised: { color: "#FF3B30", label: "COMPROMISED" },
  isolated: { color: "#A855F7", label: "ISOLATED" },
  quarantined: { color: "#FF3B30", label: "QUARANTINED" },
};

function RiskBar({ score }) {
  const color = score >= 80 ? "#FF3B30" : score >= 45 ? "#FFCC00" : "#3ECF8E";
  return (
    <div className="flex items-center gap-2 w-28">
      <div className="flex-1 h-1.5 bg-[#1F1F33]">
        <div className="h-full" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <span className="text-[11px] tabular-nums" style={{ color }}>{score}</span>
    </div>
  );
}

export default function Assets() {
  const [assets, setAssets] = useState([]);
  const [resetting, setResetting] = useState(false);
  const { query } = useSearch();

  const load = useCallback(async () => {
    const { data } = await api.get("/assets");
    setAssets(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const runAction = async (id, action) => {
    try {
      const updated = await api.post(`/assets/${id}/action`, { action });
      setAssets((p) => p.map((a) => (a.id === id ? updated.data : a)));
      const verb = { scan: "scan triggered", isolate: "isolated", quarantine: "quarantined", restore: "restored" }[action];
      toast.success(`Endpoint ${verb}`);
    } catch (e) {
      toast.error("Action failed");
    }
  };

  const resetEndpoints = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/assets/reset");
      setAssets(data);
      toast.success(`Endpoints repopulated (${data.length})`);
    } catch (e) {
      toast.error("Reset failed");
    } finally { setResetting(false); }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? assets.filter((a) =>
        [a.hostname, a.ip, a.os, a.type, a.status].join(" ").toLowerCase().includes(q))
    : assets;

  return (
    <div data-testid="assets-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">Attack Surface</p>
          <h1 className="font-head text-3xl tracking-tighter font-black mt-1">Asset & Endpoint Monitoring</h1>
        </div>
        <button data-testid="reset-endpoints-button" onClick={resetEndpoints} disabled={resetting}
          className="flex items-center gap-2 border border-[#00F0FF]/50 text-[#00F0FF] uppercase tracking-[0.15em] text-[11px] px-4 py-2.5 hover:bg-[#00F0FF]/10 active:scale-[0.98] transition-all disabled:opacity-50">
          <ArrowsCounterClockwise size={14} className={resetting ? "pulse-dot" : ""} /> {resetting ? "Resetting…" : "Reset Endpoints"}
        </button>
      </div>

      <div className="bg-[#0C0C14] border border-[#1F1F33] rounded-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="assets-table">
            <thead>
              <tr className="border-b border-[#1F1F33] text-[10px] uppercase tracking-[0.15em] text-[#71717A]">
                <th className="px-5 py-3 font-medium">Host</th>
                <th className="px-5 py-3 font-medium">IP Address</th>
                <th className="px-5 py-3 font-medium hidden md:table-cell">OS</th>
                <th className="px-5 py-3 font-medium hidden sm:table-cell">Type</th>
                <th className="px-5 py-3 font-medium">Risk</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => {
                const st = STATUS[a.status] || STATUS.healthy;
                return (
                  <tr
                    key={a.id}
                    data-testid={`asset-row-${a.hostname}`}
                    className={`border-b border-[#1F1F33] text-sm hover:bg-white/[0.03] transition-colors ${i % 2 ? "bg-white/[0.02]" : ""}`}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {a.type === "Server" ? <HardDrives size={16} weight="duotone" color="#71717A" /> : <Desktop size={16} weight="duotone" color="#71717A" />}
                        <span className="font-medium">{a.hostname}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-[#A1A1AA] tabular-nums">{a.ip}</td>
                    <td className="px-5 py-3 text-[#A1A1AA] hidden md:table-cell">{a.os}</td>
                    <td className="px-5 py-3 text-[#71717A] hidden sm:table-cell">{a.type}</td>
                    <td className="px-5 py-3"><RiskBar score={a.risk_score} /></td>
                    <td className="px-5 py-3">
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold border"
                        style={{ color: st.color, borderColor: st.color, backgroundColor: `${st.color}1A` }}
                      >
                        <span className="w-1.5 h-1.5" style={{ backgroundColor: st.color }} />
                        {st.label}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button data-testid={`scan-${a.hostname}`} onClick={() => runAction(a.id, "scan")} title="Scan"
                          className="p-1.5 border border-[#1F1F33] text-[#71717A] hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"><MagnifyingGlass size={13} /></button>
                        {(a.status === "isolated" || a.status === "quarantined") ? (
                          <button data-testid={`restore-${a.hostname}`} onClick={() => runAction(a.id, "restore")} title="Restore"
                            className="p-1.5 border border-[#1F1F33] text-[#71717A] hover:border-[#3ECF8E] hover:text-[#3ECF8E] transition-colors"><ArrowsClockwise size={13} /></button>
                        ) : (
                          <>
                            <button data-testid={`isolate-${a.hostname}`} onClick={() => runAction(a.id, "isolate")} title="Isolate"
                              className="p-1.5 border border-[#1F1F33] text-[#71717A] hover:border-[#A855F7] hover:text-[#A855F7] transition-colors"><ShieldSlash size={13} /></button>
                            <button data-testid={`quarantine-${a.hostname}`} onClick={() => runAction(a.id, "quarantine")} title="Quarantine"
                              className="p-1.5 border border-[#1F1F33] text-[#71717A] hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"><Prohibit size={13} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
